import { useState } from "react";
import { X, Scale, ShieldCheck, Cookie, Users, Copyright, ReceiptText } from "lucide-react";

const COMPANY = "MiSt / Misteck";
const CONTACT = "support@misteck.com";
const EFFECTIVE = "Effective 30 June 2026";

// Boilerplate legal copy — a starting template. Have qualified counsel review
// before relying on it commercially.
const DOCS = {
  terms: {
    icon: Scale,
    title: "Terms of Service",
    body: [
      ["1. Acceptance", `By creating an account or using ${COMPANY} ("the Platform"), you agree to these Terms. If you do not agree, do not use the Platform.`],
      ["2. Your Account", "You must provide accurate information and are responsible for activity under your account. You must be of legal age in your jurisdiction to enter binding contracts."],
      ["3. Creator Content", "You retain ownership of content you create. By publishing, you grant the Platform a worldwide, non-exclusive licence to host, display and distribute it for the purpose of operating the service. You are solely responsible for content you generate, upload or share."],
      ["4. Credits, Coins & Payments", "Generation tools consume credits/coins. Purchased credits are non-refundable except as required by law. Prices, limits and margins may change with notice."],
      ["5. Acceptable Use", "You may not use the Platform for illegal, infringing, harmful, or abusive purposes, or to generate content that violates our Community Guidelines."],
      ["6. Termination", "We may suspend or terminate accounts that breach these Terms. You may close your account at any time."],
      ["7. Disclaimer & Liability", "The Platform is provided \"as is\" without warranties. To the maximum extent permitted by law, we are not liable for indirect or consequential losses."],
    ],
  },
  privacy: {
    icon: ShieldCheck,
    title: "Privacy Policy",
    body: [
      ["Data We Collect", "Account details (name, email), usage data, generated assets, and payment metadata. We do not sell your personal data."],
      ["How We Use It", "To operate and improve the service, process payments, provide support, and maintain security and fraud prevention."],
      ["Storage & Security", "Sensitive credentials in your vault are encrypted. We apply reasonable technical and organisational measures to protect your data."],
      ["Your Rights", "You may request access, correction, export, or deletion of your personal data by contacting us."],
      ["Third Parties", "We share limited data with processors (e.g. payment and AI-generation providers) strictly to deliver the service."],
      ["Contact", `Privacy requests: ${CONTACT}`],
    ],
  },
  cookies: {
    icon: Cookie,
    title: "Cookie Policy",
    body: [
      ["What We Use", "We use essential storage (session tokens, preferences) to keep you signed in and remember settings. Analytics may be used to understand usage."],
      ["Managing Cookies", "You can clear local storage / cookies from your browser at any time. Disabling essential storage may break sign-in."],
    ],
  },
  community: {
    icon: Users,
    title: "Community Guidelines",
    body: [
      ["Be Respectful", "No harassment, hate speech, or targeted abuse."],
      ["Keep It Legal", "No illegal content, CSAM, non-consensual imagery, or content that infringes others' rights."],
      ["No Deceptive Media", "Do not use AI generation to impersonate, defraud, or spread harmful misinformation."],
      ["Enforcement", "Violations may lead to content removal, credit forfeiture, or account termination."],
    ],
  },
  copyright: {
    icon: Copyright,
    title: "Copyright & DMCA",
    body: [
      ["Respecting IP", "Only upload or generate content you have the right to use. Respect trademarks, likeness, and copyright."],
      ["Takedown Requests", `If you believe content infringes your rights, send a notice to ${CONTACT} with the work, the infringing URL, and a good-faith statement.`],
      ["Repeat Infringers", "Accounts with repeated valid infringement claims will be terminated."],
    ],
  },
  refund: {
    icon: ReceiptText,
    title: "Billing & Refunds",
    body: [
      ["Subscriptions", "Plans renew automatically until cancelled. Cancel anytime to stop future renewals; access continues to the end of the paid period."],
      ["Credits", "Consumable credits/coins are generally non-refundable once purchased, except where required by consumer law."],
      ["Chargebacks", "Please contact support before initiating a chargeback so we can resolve billing issues directly."],
      ["Contact", `Billing help: ${CONTACT}`],
    ],
  },
};

const LINKS = [
  { key: "terms", label: "Terms" },
  { key: "privacy", label: "Privacy" },
  { key: "cookies", label: "Cookies" },
  { key: "community", label: "Guidelines" },
  { key: "copyright", label: "Copyright" },
  { key: "refund", label: "Billing" },
];

// Build a plain-text version of a legal doc (for the vault download buttons).
export function legalDocText(docKey) {
  const doc = DOCS[docKey];
  if (!doc) return "";
  const lines = [doc.title.toUpperCase(), EFFECTIVE, `© ${new Date().getFullYear()} ${COMPANY}`, ""];
  doc.body.forEach(([h, p]) => { lines.push(h.toUpperCase()); lines.push(p); lines.push(""); });
  lines.push("This document is a template summary and does not constitute legal advice.");
  return lines.join("\n");
}
export { DOCS as LEGAL_DOCS };

export function LegalModal({ docKey, onClose }) {
  const doc = DOCS[docKey];
  if (!doc) return null;
  const Icon = doc.icon;
  return (
    <div className="fixed inset-0 z-[90] bg-black/85 flex items-end justify-center" data-testid="legal-modal" onClick={onClose}>
      <div
        className="w-full max-w-[412px] bg-[#0d0d0d] border-t-4 border-[#FFD028] rounded-t-3xl max-h-[82dvh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="shrink-0 flex items-center gap-2.5 px-4 py-3.5 border-b-2 border-[#FFD028]/20">
          <span className="w-9 h-9 grid place-items-center rounded-xl border-2 border-[#FFD028] bg-[#FFD028]/10">
            <Icon size={17} className="text-[#FFD028]" />
          </span>
          <div className="flex-1 leading-none">
            <h3 className="font-serif text-xl text-white">{doc.title}</h3>
            <p className="font-mono text-[8px] uppercase tracking-widest text-neutral-500 mt-1">{EFFECTIVE}</p>
          </div>
          <button onClick={onClose} data-testid="legal-modal-close" aria-label="Close"
            className="w-8 h-8 grid place-items-center border-2 border-[#FFD028] text-[#FFD028] rounded-lg active:scale-95">
            <X size={15} />
          </button>
        </div>
        <div className="flex-1 min-h-0 overflow-y-auto no-scrollbar px-4 py-4 space-y-4">
          {doc.body.map(([h, p], i) => (
            <div key={i}>
              <p className="font-mono text-[10px] font-black uppercase tracking-widest text-[#FFD028] mb-1">{h}</p>
              <p className="font-mono text-[11px] leading-relaxed text-neutral-300">{p}</p>
            </div>
          ))}
          <p className="font-mono text-[9px] leading-relaxed text-neutral-500 pt-2 border-t border-neutral-800">
            This document is a template summary and does not constitute legal advice. © {new Date().getFullYear()} {COMPANY}.
          </p>
        </div>
      </div>
    </div>
  );
}

export default function LandingLegalFooter() {
  const [open, setOpen] = useState(null);
  return (
    <footer className="px-4 pt-4 pb-6 mt-2 border-t border-neutral-800" data-testid="landing-legal-footer">
      <div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-2">
        {LINKS.map((l, i) => (
          <span key={l.key} className="flex items-center gap-3">
            <button
              onClick={() => setOpen(l.key)}
              data-testid={`legal-link-${l.key}`}
              className="font-mono text-[10px] uppercase tracking-widest text-neutral-400 hover:text-[#FFD028] transition-colors"
            >
              {l.label}
            </button>
            {i < LINKS.length - 1 && <span className="text-neutral-700 text-[10px]">•</span>}
          </span>
        ))}
      </div>
      <p className="mt-3 text-center font-mono text-[9px] tracking-widest text-neutral-600">
        © {new Date().getFullYear()} {COMPANY.toUpperCase()} — ALL RIGHTS RESERVED
      </p>
      <p className="mt-1 text-center font-mono text-[9px] tracking-widest text-neutral-700">
        {CONTACT}
      </p>
      {open && <LegalModal docKey={open} onClose={() => setOpen(null)} />}
    </footer>
  );
}
